import { createApp, ref } from 'vue'
import { createPinia } from 'pinia'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import './style.css'
import App from './App.vue'
import { setTableStore } from '@tag-and-tally/shared-ui'
import { webTableStore } from './table-data'
import { registerWebIcons } from './icons'

// Storage key names predate the Forge → Sparks rename; changing them would reset saved settings.
const VERSION_STORAGE_KEY = 'tag-tally-forge-version'
const currentVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0'

async function clearServiceWorkerAndCaches(): Promise<void> {
  if ('serviceWorker' in navigator) {
    const regs = await navigator.serviceWorker.getRegistrations()
    await Promise.all(regs.map((reg) => reg.unregister()))
  }
  if ('caches' in window) {
    const keys = await caches.keys()
    await Promise.all(keys.map((k) => caches.delete(k)))
  }
}

async function maybeClearAndReloadForNewVersion(): Promise<boolean> {
  const stored = localStorage.getItem(VERSION_STORAGE_KEY)
  if (stored != null && stored !== currentVersion) {
    await clearServiceWorkerAndCaches()
    localStorage.setItem(VERSION_STORAGE_KEY, currentVersion)
    window.location.reload()
    return true
  }
  localStorage.setItem(VERSION_STORAGE_KEY, currentVersion)
  return false
}

registerWebIcons()

const pinia = createPinia()
const app = createApp(App)

app.component('FontAwesomeIcon', FontAwesomeIcon)
setTableStore(webTableStore)
app.provide('tableStore', webTableStore)

const pwaNeedRefresh = ref(false)
let pwaReloadFn: (() => void) | null = null
app.provide('pwaNeedRefresh', pwaNeedRefresh)
app.provide('pwaReload', () => {
  if (pwaReloadFn) pwaReloadFn()
})

;(async () => {
  const didReload = await maybeClearAndReloadForNewVersion()
  if (didReload) return

  app.use(pinia).mount('#app')

  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    const { registerSW } = await import('virtual:pwa-register')
    registerSW({
      onNeedRefresh(reload) {
        pwaReloadFn = reload
        pwaNeedRefresh.value = true
      },
      onOfflineReady() {},
    })
  }
})()
